import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiCheckBold,
    mdiAccountGroup,
    mdiCalendarClock,
    mdiTicketConfirmationOutline,
    mdiCreditCardOutline,
    mdiEmailOutline,
    mdiChevronDoubleRight,
} from "@mdi/js";

import { useAuth } from "../contexts/AuthContext";
import StoreBanner from "../components/store-banner";
import { useMobileMenu } from "../hooks/useMobileMenu";

import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import "./Features.css";
import "./header.css";
import "./Common.css";

const CLASS_FEATURES = [
    "Upload your school's existing students via spreadsheet",
    "Landing pages with your branding colours, custom URL links",
    "Class Instructor session tracking with reports",
    "Student enrolment status on mobile app and students web hub",
    "Instalment tracking (weekly, fortnightly, or monthly)",
    "Walk-in registration with on-the-spot payment",
    "Tablet ready touchscreen check-in interface",
    "Attendance tracking and history",
    "Term rollover into the next term",
    "Student roster with payment status visibility",
    "Multi-user team access with roles (Owner, Admin, Manager, Helper, Staff)",
    "Short Course and small congress/weekender support",
];

const PRIVATE_FEATURES = [
    "Public instructor profile, searchable by style and locations",
    "Independent controls for search visibility and accepting public bookings",
    "Multi travel location support with per-location venue-hire pricing",
    "Students can book through mobile app or website",
    "Google Calendar-style weekly availability",
    "Travel-period availability for different cities and currencies",
    "Holiday, sick-day blocking, and auto cancellation messaging",
    "Normal schedule publishing from one week to twelve months",
    "Booking refunds and platform credit",
    "Instructor and student messaging across web and mobile app",
    "Automatic cancellation app notifications",
];

const EVENT_FEATURES = [
    "Event creation with your own colour branded event page",
    "Multiple ticket tiers with quantity caps",
    "Instant digital tickets with QR codes",
    "Mobile QR scanning at the door",
    "Email confirmation with an embedded ticket",
    "Sales-window notifications on web links",
    "Bulk event-level refunds",
    "Per-event sales analytics",
    "Public shareable event pages with social media optimisation",
];

const PAYMENT_FEATURES = [
    "Stripe Connect payouts to your bank",
    "Card payments via Apple Pay and Google Pay",
    "Bank transfer option (students pay you directly, with no online service fee)",
    "Reconciliation page to check external payments for student status",
    "Support multi-currency for your business",
    "Automatic payment receipts",
    "Financial dashboard and ledger",
];

const ADMIN_FEATURES = [
    "Rich-text bulk emails, customisable with inline images/text",
    "Send emails to all students, individuals, or single classes",
    "Send-status tracking",
    "Branding customisation on your public pages",
    "Branded enquiry and enrolment pages",
    "Spreadsheet student import",
    "Mobile app notifications and in-app messaging",
];

const DOMAIN_SECTIONS = [
    {
        icon: mdiAccountGroup,
        title: "Classes",
        framing: "Run terms, classes, and enrolments from one roster.",
        features: CLASS_FEATURES,
    },
    {
        icon: mdiCalendarClock,
        title: "Privates",
        framing: "Publish your availability and take private-lesson bookings.",
        features: PRIVATE_FEATURES,
    },
    {
        icon: mdiTicketConfirmationOutline,
        title: "Events",
        framing: "Sell tickets with easy mobile QR check-in at the door.",
        features: EVENT_FEATURES,
    },
];

const CROSS_CARDS = [
    { icon: mdiCreditCardOutline, title: "Payments", features: PAYMENT_FEATURES },
    { icon: mdiEmailOutline, title: "Communication and Admin", features: ADMIN_FEATURES },
];

const renderRow = (feature, idx) => (
    <li key={idx} className="features-list-item">
        <span className="features-list-icon">
            <Icon path={mdiCheckBold} size={0.7} color="var(--dl-color-gray-white)" />
        </span>
        <span>{feature}</span>
    </li>
);

const Features = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const handleLogout = () => {
        logout();
    };

    useMobileMenu();

    return (
        <div className="features-page">
            {/* React 19 native title: browser-tab UX only (does NOT reach OG crawlers on CRA SPA) */}
            <title>Features | Dance Localé</title>

            {/* Header - same structure as LearnMore.js */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a
                            href="/learn-more"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Learn More
                        </a>
                        <a
                            href="/how-it-works"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            How it Works
                        </a>
                        <span
                            className="home-text11"
                            style={{ color: "var(--dl-color-orange-default)", cursor: "pointer" }}
                            onClick={() => navigate("/features")}>
                            Features
                        </span>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text12"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <button
                                    onClick={handleLogout}
                                    className="home-text13"
                                    style={{ cursor: "pointer", background: "none", border: "none", color: "inherit" }}>
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text13"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon15">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a
                            href="/learn-more"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Learn More
                        </a>
                        <a
                            href="/how-it-works"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            How it Works
                        </a>
                        <span
                            className="home-text15"
                            onClick={() => navigate("/features")}
                            style={{ cursor: "pointer", color: "var(--dl-color-orange-default)" }}>
                            Features
                        </span>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text16"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <span className="home-text17" onClick={handleLogout} style={{ cursor: "pointer" }}>
                                    Logout
                                </span>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text17"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Hero */}
            <div className="features-hero content-card">
                <h1 className="features-headline">Everything Dance Localé does for classes, privates, and events</h1>
                <p className="features-subtext">
                    Here's the full feature list. See the parts that help you most.
                </p>
                <div className="features-hero-cta">
                    <a href="/register" className="home-cta-primary">
                        Start Free
                    </a>
                    <a href="/learn-more" className="features-hero-link">
                        See pricing and comparison <Icon path={mdiChevronDoubleRight} size={0.8} />
                    </a>
                </div>
            </div>

            {/* Domain sections */}
            {DOMAIN_SECTIONS.map(section => (
                <div key={section.title} className="features-section content-card">
                    <div className="features-section-head">
                        <div className="features-section-title-row">
                            <div className="features-section-icon">
                                <Icon path={section.icon} size={1.3} />
                            </div>
                            <h2 className="features-section-title">{section.title}</h2>
                        </div>
                        <p className="features-section-framing">{section.framing}</p>
                    </div>
                    <ul className="features-list">{section.features.map(renderRow)}</ul>
                </div>
            ))}

            {/* Cross-cutting section */}
            <div className="features-crosscutting content-card">
                <div className="features-section-head">
                    <h2 className="features-section-title">Payments, communication, and admin</h2>
                    <p className="features-section-framing">
                        The tools that run behind every class, lesson, and event.
                    </p>
                </div>
                <div className="features-cross-grid">
                    {CROSS_CARDS.map(card => (
                        <div key={card.title} className="features-cross-card">
                            <div className="features-cross-head">
                                <span className="features-cross-icon">
                                    <Icon path={card.icon} size={1.1} />
                                </span>
                                <h3 className="features-cross-title">{card.title}</h3>
                            </div>
                            <ul className="features-list features-list-single">{card.features.map(renderRow)}</ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* Final CTA */}
            <div className="features-final-cta">
                <h2 className="features-final-heading">Start free. No monthly fees.</h2>
                <div className="features-final-cta-row">
                    <a href="/register" className="home-cta-primary">
                        Start Free
                    </a>
                </div>
                <div className="features-final-store-banner">
                    <StoreBanner />
                </div>
            </div>

            {/* Footer - same structure as home.js */}
            <div className="home-footer">
                <footer className="home-container22">
                    <div className="home-container23">
                        <span
                            className="home-text56"
                            onClick={() => navigate("/privacy-policy")}
                            style={{ cursor: "pointer" }}>
                            Privacy policy
                        </span>
                        <span
                            className="home-text57"
                            onClick={() => navigate("/terms-conditions")}
                            style={{ cursor: "pointer" }}>
                            Terms of Service
                        </span>
                        <span
                            className="home-text58"
                            onClick={() => navigate("/contact")}
                            style={{ cursor: "pointer" }}>
                            Contact
                        </span>
                    </div>
                    <div className="home-divider"></div>
                    <div className="home-container24">
                        <span className="home-text59 Body2">
                            © {new Date().getFullYear()} Dance Localé. All rights reserved
                        </span>
                        <div className="home-container25">
                            <a
                                href="https://www.instagram.com/dancelocalehq/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link5">
                                <div className="home-container26">
                                    <svg viewBox="0 0 877.7142857142857 1024" className="home-icon25">
                                        <path d="M585.143 512c0-80.571-65.714-146.286-146.286-146.286s-146.286 65.714-146.286 146.286 65.714 146.286 146.286 146.286 146.286-65.714 146.286-146.286zM664 512c0 124.571-100.571 225.143-225.143 225.143s-225.143-100.571-225.143-225.143 100.571-225.143 225.143-225.143 225.143 100.571 225.143 225.143zM725.714 277.714c0 29.143-23.429 52.571-52.571 52.571s-52.571-23.429-52.571-52.571 23.429-52.571 52.571-52.571 52.571 23.429 52.571 52.571zM438.857 152c-64 0-201.143-5.143-258.857 17.714-20 8-34.857 17.714-50.286 33.143s-25.143 30.286-33.143 50.286c-22.857 57.714-17.714 194.857-17.714 258.857s-5.143 201.143 17.714 258.857c8 20 17.714 34.857 33.143 50.286s30.286 25.143 50.286 33.143c57.714 22.857 194.857 17.714 258.857 17.714s201.143 5.143 258.857-17.714c20-8 34.857-17.714 50.286-33.143s25.143-30.286 33.143-50.286c22.857-57.714 17.714-194.857 17.714-258.857s5.143-201.143-17.714-258.857c-8-20-17.714-34.857-33.143-50.286s-30.286-25.143-50.286-33.143c-57.714-22.857-194.857-17.714-258.857-17.714zM877.714 512c0 60.571 0.571 120.571-2.857 181.143-3.429 70.286-19.429 132.571-70.857 184s-113.714 67.429-184 70.857c-60.571 3.429-120.571 2.857-181.143 2.857s-120.571 0.571-181.143-2.857c-70.286-3.429-132.571-19.429-184-70.857s-67.429-113.714-70.857-184c-3.429-60.571-2.857-120.571-2.857-181.143s-0.571-120.571 2.857-181.143c3.429-70.286 19.429-132.571 70.857-184s113.714-67.429 184-70.857c60.571-3.429 120.571-2.857 181.143-2.857s120.571-0.571 181.143 2.857c70.286 3.429 132.571 19.429 184 70.857s67.429 113.714 70.857 184c3.429 60.571 2.857 120.571 2.857 181.143z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://www.facebook.com/DanceLocaleHQ/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link6">
                                <div className="home-container27">
                                    <svg viewBox="0 0 24 24" className="home-icon27">
                                        <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a href="https://x.com" target="_blank" rel="noreferrer noopener" className="home-link7">
                                <div className="home-container28">
                                    <svg viewBox="0 0 950.8571428571428 1024" className="home-icon29">
                                        <path d="M925.714 233.143c-25.143 36.571-56.571 69.143-92.571 95.429 0.571 8 0.571 16 0.571 24 0 244-185.714 525.143-525.143 525.143-104.571 0-201.714-30.286-283.429-82.857 14.857 1.714 29.143 2.286 44.571 2.286 86.286 0 165.714-29.143 229.143-78.857-81.143-1.714-149.143-54.857-172.571-128 11.429 1.714 22.857 2.857 34.857 2.857 16.571 0 33.143-2.286 48.571-6.286-84.571-17.143-148-91.429-148-181.143v-2.286c24.571 13.714 53.143 22.286 83.429 23.429-49.714-33.143-82.286-89.714-82.286-153.714 0-34.286 9.143-65.714 25.143-93.143 90.857 112 227.429 185.143 380.571 193.143-2.857-13.714-4.571-28-4.571-42.286 0-101.714 82.286-184.571 184.571-184.571 53.143 0 101.143 22.286 134.857 58.286 41.714-8 81.714-23.429 117.143-44.571-13.714 42.857-42.857 78.857-81.143 101.714 37.143-4 73.143-14.286 106.286-28.571z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="http://www.youtube.com/@DanceLocale"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link8">
                                <div className="home-container29">
                                    <svg viewBox="0 0 1024 1024" className="home-icon31">
                                        <path d="M406.286 644.571l276.571-142.857-276.571-144.571v287.429zM512 152c215.429 0 358.286 10.286 358.286 10.286 20 2.286 64 2.286 102.857 43.429 0 0 31.429 30.857 40.571 101.714 10.857 82.857 10.286 165.714 10.286 165.714v77.714s0.571 82.857-10.286 165.714c-9.143 70.286-40.571 101.714-40.571 101.714-38.857 40.571-82.857 40.571-102.857 42.857 0 0-142.857 10.857-358.286 10.857v0c-266.286-2.286-348-10.286-348-10.286-22.857-4-74.286-2.857-113.143-43.429 0 0-31.429-31.429-40.571-101.714-10.857-82.857-10.286-165.714-10.286-165.714v-77.714s-0.571-82.857-0.571-165.714c9.143-70.857 40.571-101.714 40.571-101.714 38.857-41.143 82.857-41.143 102.857-43.429 0 0 142.857-10.286 358.286-10.286v0z"></path>
                                    </svg>
                                </div>
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default Features;
